import React from "react";
import { Modal, Input, List, Typography } from "antd";
import {
  PlusOutlined,
  UnorderedListOutlined,
  SafetyCertificateOutlined,
  DownloadOutlined,
  ExperimentOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { downloadCompliancePassport, validateComplianceCase } from "../../api";
import { message } from "antd";
import { pushNotification } from "../ux/notificationStore";

type Action = {
  key: string;
  label: string;
  icon: React.ReactNode;
  keywords: string;
  run: () => void | Promise<void>;
};

type Props = {
  open: boolean;
  onClose: () => void;
};

export function CommandPalette({ open, onClose }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ caseId?: string }>();
  const [query, setQuery] = React.useState("");

  const caseId =
    params.caseId ||
    (location.pathname.match(/\/dds\/cases\/([^/]+)/) || [])[1] ||
    undefined;

  const actions: Action[] = React.useMemo(() => {
    const base: Action[] = [
      {
        key: "new",
        label: t("compliance.commandPalette.newCase"),
        icon: <PlusOutlined />,
        keywords: "new case create",
        run: () => navigate("/dds/new"),
      },
      {
        key: "list",
        label: t("compliance.commandPalette.caseList"),
        icon: <UnorderedListOutlined />,
        keywords: "list cases home",
        run: () => navigate("/dds"),
      },
      {
        key: "demo",
        label: t("compliance.commandPalette.demoPreset"),
        icon: <ExperimentOutlined />,
        keywords: "demo preset timber de",
        run: () => navigate("/dds/new?preset=timber-de"),
      },
      {
        key: "investor",
        label: t("investor.demo.button"),
        icon: <ExperimentOutlined />,
        keywords: "investor demo pitch narration",
        run: () => navigate("/dds?investorDemo=1"),
      },
    ];
    if (caseId) {
      base.push({
        key: "validate",
        label: t("compliance.commandPalette.validate"),
        icon: <SafetyCertificateOutlined />,
        keywords: "validate run",
        run: async () => {
          try {
            await validateComplianceCase(caseId);
            message.success(t("compliance.caseDetail.validationComplete"));
            pushNotification({
              kind: "validation_done",
              title: t("compliance.notifications.validationDone"),
              caseId,
            });
          } catch {
            message.error(t("compliance.caseDetail.validationFailed"));
          }
        },
      });
      base.push({
        key: "download",
        label: t("compliance.commandPalette.download"),
        icon: <DownloadOutlined />,
        keywords: "download passport zip",
        run: async () => {
          try {
            await downloadCompliancePassport(caseId);
          } catch {
            message.error(t("compliance.passport.downloadFailed"));
          }
        },
      });
    }
    return base;
  }, [t, navigate, caseId]);

  const filtered = actions.filter((a) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return a.label.toLowerCase().includes(q) || a.keywords.includes(q);
  });

  React.useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={t("compliance.commandPalette.title")}
      destroyOnHidden
      data-testid="command-palette"
    >
      <Input
        autoFocus
        allowClear
        placeholder={t("compliance.commandPalette.placeholder")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onPressEnter={() => {
          if (filtered[0]) {
            void Promise.resolve(filtered[0].run()).finally(onClose);
          }
        }}
        style={{ marginBottom: 12 }}
      />
      <List
        dataSource={filtered}
        locale={{ emptyText: t("compliance.commandPalette.empty") }}
        renderItem={(item) => (
          <List.Item
            style={{ cursor: "pointer" }}
            onClick={() => {
              void Promise.resolve(item.run()).finally(onClose);
            }}
          >
            <Typography.Text>
              <span style={{ marginRight: 8 }}>{item.icon}</span>
              {item.label}
            </Typography.Text>
          </List.Item>
        )}
      />
    </Modal>
  );
}

export function useCommandPaletteHotkey(setOpen: (v: boolean) => void) {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);
}
